import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import MonthlyCard from '../components/cards/MonthlyCard';
import PanoramaVideoCard from '../components/cards/PanoramaVideoCard';
import MonthlyFolderCard from '../components/common/MonthlyFolderCard';
import SiteMap from '../components/common/SiteMap';
import EmptyState from '../components/common/EmptyState';
import { FiCalendar, FiInfo, FiX, FiMaximize2, FiDownload, FiCompass, FiChevronLeft, FiFolder } from 'react-icons/fi';

const ThreeSixtyTour = () => {
  const { site, monthlyUpdates = [] } = useOutletContext() || {};
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // Month level
  const [selectedFolder, setSelectedFolder] = useState(null); // Folder level

  // Extract all panoramas from monthly updates
  const panoramas = monthlyUpdates.reduce((acc, update) => {
    if (update.panoramas && update.panoramas.length > 0) {
      update.panoramas.forEach((pano) => {
        acc.push({
          ...pano,
          month: update.month,
          year: update.year,
          progress: update.progress_percentage,
          notes: update.notes,
          update_date: update.update_date,
        });
      });
    }
    return acc;
  }, []);

  // ✅ Group 360° videos by month/year
  const months = monthlyUpdates.reduce((acc, update) => {
    const videos = update.videos?.filter(v => v.video_type === '360' || v.is_360) || [];
    if (videos.length > 0) {
      const folderSet = new Set(videos.map(v => v.custom_folder_name || 'General'));
      acc.push({
        id: `${update.update_id}-360`,
        updateId: update.update_id,
        month: update.month,
        year: update.year,
        videos: videos,
        count: videos.length,
        folderCount: folderSet.size,
        progress: update.progress_percentage,
        notes: update.notes,
      });
    }
    return acc;
  }, []).sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  // ✅ Group by custom folder name within month
  const folders = selectedMonth
    ? Object.values(
      selectedMonth.videos.reduce((acc, vid) => {
        const key = vid.custom_folder_name || 'General';
        if (!acc[key]) {
          acc[key] = { folderName: key, videos: [] };
        }
        acc[key].videos.push(vid);
        return acc;
      }, {})
    )
    : [];

  const handleOpenTour = (pano) => setSelectedTour(pano);
  const handleCloseTour = () => setSelectedTour(null);

  const handleOpen360VideoTab = (videoUrl) => {
    if (videoUrl) {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getMonthName = (month) => {
    const names = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return names[month - 1] || `Month ${month}`;
  };

  // ✅ LEVEL 3: Show videos inside selected custom folder
  if (selectedFolder) {
    return (
      <div>
        <button
          onClick={() => setSelectedFolder(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to folders
        </button>

        <div className="section-title">
          <div className="section-title__icon">
            <FiFolder />
          </div>
          <div>
            <h2>{selectedFolder.folderName}</h2>
            <p>{selectedFolder.videos.length} video{selectedFolder.videos.length !== 1 ? 's' : ''} available</p>
          </div>
        </div>

        <div className="portal-grid portal-grid--two">
          {selectedFolder.videos.map((vid) => (
            <PanoramaVideoCard
              key={vid.video_id}
              month={selectedMonth.month}
              year={selectedMonth.year}
              title={vid.title}                          // ✅ ADDED: Title now shows
              notes={vid.notes}
              thumbnail={vid.thumbnail_url}
              videoSource={vid.video_source}
              onClick={() => handleOpen360VideoTab(vid.video_url)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ✅ LEVEL 2: Show folders inside selected month
  if (selectedMonth) {
    return (
      <div>
        <button
          onClick={() => setSelectedMonth(null)}
          className="btn-secondary py-sm px-md flex items-center gap-xs text-sm mb-md text-textPrimary hover:bg-bgLight"
        >
          <FiChevronLeft />
          Back to all months
        </button>

        <div className="section-title">
          <div className="section-title__icon">
            <FiCompass />
          </div>
          <div>
            <h2>360° Videos - {getMonthName(selectedMonth.month)} {selectedMonth.year}</h2>
            <p>{folders.length} folder{folders.length !== 1 ? 's' : ''} • {selectedMonth.count} videos total</p>
          </div>
        </div>

        <div className="space-y-3">
          {folders.map((folder) => (
            <MonthlyFolderCard
              key={folder.folderName}
              month={selectedMonth.month}
              year={selectedMonth.year}
              customName={folder.folderName}
              count={folder.videos.length}
              isVideo={true}
              onClick={() => setSelectedFolder(folder)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-xl">
      {/* 360° Panoramas Section */}
      {/* <div>
        <div className="section-title">
          <div className="section-title__icon">
            <FiCalendar />
          </div>
          <div>
            <h2>Panorama Tour</h2>
            <p>Track site progress through monthly 360° virtual panoramas.</p>
          </div>
        </div>

        {panoramas.length === 0 ? (
          <EmptyState message="No 360° panoramas have been uploaded for this site yet." />
        ) : (
          <div className="portal-grid portal-grid--two">
            {panoramas.map((pano) => (
              <MonthlyCard
                key={pano.panorama_id}
                month={pano.month}
                year={pano.year}
                title={pano.title}
                progress={pano.progress}
                notes={pano.notes}
                thumbnail={pano.thumbnail_url || pano.tour_url}
                type="360"
                onClick={() => handleOpenTour(pano)}
              />
            ))}
          </div>
        )}

        {panoramas.length > 0 && (
          <div className="info-bar mt-md">
            <FiInfo /> Select any month to view the 360° panorama image and project progress.
          </div>
        )}
      </div> */}

      {/* 360° Video Tours Section - Monthly Folders like ImageProduct */}

      <div className="pt-lg border-t border-borderLight">
        <div className="section-title">
          <div className="section-title__icon">
            <FiCompass />
          </div>
          <div>
            <h2>360° Interactive Video Tours</h2>
            <p>Select any month to view 360° videos.</p>
          </div>
        </div>

        {months.length === 0 ? (
          <EmptyState message="No 360° video tours uploaded for this site yet." />
        ) : (
          <div className="space-y-3">
            {months.map((month) => (
              <MonthlyFolderCard
                key={month.id}
                month={month.month}
                year={month.year}
                count={month.count}
                summary={`${month.folderCount} folder${month.folderCount !== 1 ? 's' : ''}, ${month.count} video${month.count !== 1 ? 's' : ''}`}
                isVideo={true}
                onClick={() => setSelectedMonth(month)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Embedded Google Map Section */}
      {/* <div className="pt-lg border-t border-borderLight">
        <SiteMap
          latitude={site?.latitude}
          longitude={site?.longitude}
          siteName={site?.site_name}
          googleMapsUrl={site?.google_maps_url}
        />
      </div> */}

      {/* 360 Panorama Lightbox Overlay */}
      {/* {selectedTour && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 md:p-8 animate-fade-in"
          onClick={handleCloseTour}
        >
          <div
            className="w-full max-w-6xl flex justify-between items-center text-white mb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="font-bold text-base md:text-xl leading-tight">
                {selectedTour.title || `Panorama ${getMonthName(selectedTour.month)} ${selectedTour.year}`}
              </h3>
              <p className="text-sm text-gray-400">
                {getMonthName(selectedTour.month)} {selectedTour.year} • Progress: {selectedTour.progress}%
              </p>
              {selectedTour.notes && (
                <p className="text-xs text-gray-500 mt-1 max-w-lg">
                  📝 {selectedTour.notes}
                </p>
              )}
            </div>
            <button
              onClick={handleCloseTour}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors duration-150"
              title="Close"
            >
              <FiX size={24} />
            </button>
          </div>

          <div
            className="w-full max-w-6xl bg-zinc-900 rounded-lg overflow-hidden border border-gray-700 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedTour.tour_url || selectedTour.thumbnail_url}
                alt={selectedTour.title || `Panorama ${selectedTour.month}/${selectedTour.year}`}
                className="w-full h-auto max-h-[75vh] object-contain bg-zinc-900"
                loading="lazy"
              />
              <div className="absolute bottom-4 right-4 flex gap-2">
                <button
                  onClick={() => window.open(selectedTour.tour_url || selectedTour.thumbnail_url, '_blank')}
                  className="p-2 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors duration-150"
                  title="Open in new tab"
                >
                  <FiMaximize2 size={18} />
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedTour.tour_url || selectedTour.thumbnail_url;
                    link.download = `${selectedTour.title || 'panorama'}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="p-2 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors duration-150"
                  title="Download image"
                >
                  <FiDownload size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ThreeSixtyTour;